import { useState } from 'react';
import axios from 'axios';
import { Trash, Trophy } from '@phosphor-icons/react';
import TournamentFilterBar from './TournamentFilterBar';
import TournamentModerationCard from './TournamentModerationCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArchiveConfirmModal = ({ pending, onCancel, onConfirm }) => {
  if (!pending) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="delete-confirmation-modal">
      <div className="bg-white rounded-2xl p-7 max-w-md w-full shadow-2xl">
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <Trash size={22} weight="bold" className="text-red-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Archive this tournament?</h2>
        <p className="text-sm text-gray-600 mb-2">
          Remove <span className="font-semibold text-gray-900">&ldquo;{pending.name}&rdquo;</span> from public listings and organizer dashboards.
        </p>
        <p className="text-sm text-gray-600 mb-5">
          Registrations, payment screenshots, and team rosters are <strong>preserved</strong> for audit history.
        </p>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-900 px-4 py-2.5 rounded-xl text-sm font-semibold"
            data-testid="cancel-delete-btn"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold"
            data-testid="confirm-delete-btn"
          >
            Archive tournament
          </button>
        </div>
      </div>
    </div>
  );
};

const RejectDeletionModal = ({ pending, onCancel, onConfirm }) => {
  if (!pending) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-7 max-w-md w-full shadow-2xl">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Reject deletion request?</h2>
        <p className="text-sm text-gray-600 mb-5">
          This will restore <span className="font-semibold text-gray-900">&ldquo;{pending.name}&rdquo;</span> to Approved status. The organizer can request deletion again later.
        </p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-900 px-4 py-2.5 rounded-xl text-sm font-semibold">Cancel</button>
          <button onClick={onConfirm} className="flex-1 bg-gray-800 hover:bg-black text-white px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid="confirm-reject-deletion-btn">Reject Request</button>
        </div>
      </div>
    </div>
  );
};

const AdminTournamentsTab = ({ tournaments, tournamentFilter, setTournamentFilter, onApprove, onReject, onDelete }) => {
  const [pendingDelete, setPendingDelete] = useState(null);
  const [rejectingDeletion, setRejectingDeletion] = useState(null);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    await onDelete(pendingDelete.tournament_id);
    setPendingDelete(null);
  };

  const confirmRejectDeletion = async () => {
    if (!rejectingDeletion) return;
    try {
      await axios.post(`${API}/admin/tournaments/${rejectingDeletion.tournament_id}/reject-deletion`, {}, { withCredentials: true });
      setRejectingDeletion(null);
      setTournamentFilter('approved');
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed to reject deletion');
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <TournamentFilterBar value={tournamentFilter} onChange={setTournamentFilter} />

      {tournaments.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
          <Trophy size={48} weight="duotone" className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No tournaments in this category</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tournaments.map((t, i) => (
            <TournamentModerationCard
              key={t.tournament_id}
              tournament={t}
              index={i}
              onApprove={onApprove}
              onReject={onReject}
              onRequestArchive={setPendingDelete}
              onRequestRejectDeletion={setRejectingDeletion}
            />
          ))}
        </div>
      )}

      <ArchiveConfirmModal
        pending={pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />

      <RejectDeletionModal
        pending={rejectingDeletion}
        onCancel={() => setRejectingDeletion(null)}
        onConfirm={confirmRejectDeletion}
      />
    </div>
  );
};

export default AdminTournamentsTab;
